// Microphone, camera and speaker choice for calls. Kept per device (browser storage),
// since device ids only mean something on the machine that reported them. Browsers also
// rotate ids (Safari on every load; others without persistent permission), so the label
// is kept too and used to find the same device again.

export type AvKind = 'audioinput' | 'videoinput' | 'audiooutput'

export interface AvDevice {
  id: string
  kind: AvKind
  label: string
}

export interface AvPick {
  id: string
  label: string
}

export type AvChoice = Partial<Record<AvKind, AvPick>>

const KEY = 'hearth:av-devices'
const NOUN: Record<AvKind, string> = { audioinput: 'Microphone', videoinput: 'Camera', audiooutput: 'Speaker' }

export function loadChoice(): AvChoice {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}') as unknown
    if (v && typeof v === 'object') {
      const out: AvChoice = {}
      for (const k of ['audioinput', 'videoinput', 'audiooutput'] as const) {
        const p = (v as Record<string, unknown>)[k] as Partial<AvPick> | null | undefined
        if (p && typeof p.id === 'string' && p.id) out[k] = { id: p.id, label: typeof p.label === 'string' ? p.label : '' }
      }
      return out
    }
  } catch {
    // storage unavailable or corrupt
  }
  return {}
}

export function saveChoice(c: AvChoice): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(c))
  } catch {
    // storage unavailable: the choice lasts until reload
  }
}

/** The devices of one kind, in the browser's order, with a readable label for each. */
export function devicesOf(all: readonly Pick<MediaDeviceInfo, 'deviceId' | 'kind' | 'label'>[], kind: AvKind): AvDevice[] {
  const seen = new Set<string>()
  const out: AvDevice[] = []
  for (const d of all) {
    // Without permission browsers report one anonymous entry per kind; it can't be chosen.
    if (d.kind !== kind || !d.deviceId || seen.has(d.deviceId)) continue
    seen.add(d.deviceId)
    out.push({ id: d.deviceId, kind, label: d.label || `${NOUN[kind]} ${out.length + 1}` })
  }
  return out
}

/**
 * The device to use: the stored choice if it's still plugged in (by id, or by name when the
 * browser has rotated ids), else the system default.
 */
export function resolveDevice(list: readonly AvDevice[], stored: AvPick | undefined): string | undefined {
  if (stored) {
    const hit = list.find((d) => d.id === stored.id) ?? (stored.label ? list.find((d) => d.label === stored.label) : undefined)
    if (hit) return hit.id
  }
  return list.find((d) => d.id === 'default')?.id ?? list[0]?.id
}
